<template>
  <div>
    <Button
      v-if="showButton"
      label="Add Note"
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="mdi-plus" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Create Note"
      :style="{ width: 'min(50rem, 95vw)' }"
      :modal="true"
    >
      <Tabs value="0">
        <TabList>
          <Tab value="0">
            <div class="flex items-center gap-1">
              <i class="pi pi-pen-to-square" />
              Note
            </div>
          </Tab>

          <Tab value="1">
            <div class="flex items-center gap-1">
              <i class="pi pi-paperclip" />
              Attachments
            </div>
          </Tab>

          <Tab
            value="2"
          >
            <div class="flex items-center gap-1">
              <i class="pi pi-share-alt" />
              Associations
            </div>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel value="0">
            <div class="flex flex-col gap-4 h-80">
              <div class="flex flex-col gap-2">
                <label for="type">
                  Type
                  <span class="text-red-500">*</span>
                </label>

                <Dropdown
                  id="type"
                  v-model="noteData.type"
                  :options="typeOptions"
                  optionLabel="name"
                  optionValue="value"
                  placeholder="Select type"
                  class="w-full"
                />
              </div>

              <div class="flex flex-1 flex-col gap-2">
                <label for="content">
                  Content
                  <span class="text-red-500">*</span>
                </label>

                <WidgetsRichTextEditor
                  v-model:data="noteData.content"
                  class="!h-[100px]"
                  :isEditing="true"
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel value="1">
            <div class="h-80">
              <UploaderPublicStorage
                ref="uploaderRef"
                v-model:selectedFiles="stagedFiles"
                v-model:uploadedFiles="uploadedFiles"
                v-model:isSubmitting="isSubmitting"
                v-model:filesPath="filesPath"
              />
            </div>
          </TabPanel>

          <TabPanel value="2">
            <div class="flex flex-col gap-4 h-80">
              <div
                class="flex flex-col gap-2"
              >
                <label for="type">
                  Contact
                  <span class="text-xs">[optional]</span>
                </label>

                <ContactSelectSingle
                  v-model:selectedContact="selectedContact"
                  :showCreateContact="false"
                  :isDisabled="props.contactId ? true : false"
                />
              </div>

              <div
                class="flex flex-col gap-2"
              >
                <label for="type">
                  Business
                  <span class="text-xs">[optional]</span>
                </label>

                <Select
                  v-model="selectedBusiness"
                  class="w-full"
                  :options="businesses"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select business"
                  filter
                  filterPlaceholder="Search businesses"
                  :disabled="props.businessId"
                />
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <template #footer>
        <div class="w-full flex justify-between items-center px-5">
          <div class="flex gap-2 items-center">
            <Checkbox
              v-model="noteData.showOnVendorReport"
              binary
            />

            <label for="showOnVendorReport">
              Show Note On Vendor Report
            </label>
          </div>

          <div class="flex gap-2 items-center">
            <Button
              outlined
              label="Cancel"
              icon="pi pi-times"
              @click="hideDialog"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="loading"
              @click="saveNote"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Business } from '@prisma/client'

const props = defineProps<{
  contactId?: string
  businessId?: string
  contractId?: string
}>()

const emit = defineEmits(['refresh'])

const { createNote } = useNotes()
const toast = useToast()
const route = useRoute()

// Dialog
const visible = ref(false)

const showButton = defineModel<boolean>('showButton', {
  required: false,
  default: true,
})

const toggleOpen = async () => {
  visible.value = true
  await init()
}

defineExpose({
  toggleOpen,
})

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  resetForm()
}

// Form
const loading = ref(false)
const submitted = ref(false)

const noteData = ref({
  type: 'Note',
  content: '',
  showOnVendorReport: true,
})

const errors = ref({
  type: false,
  content: false,
})

const resetForm = () => {
  noteData.value = {
    type: 'Note',
    content: '',
    showOnVendorReport: true,
  }
  errors.value = {
    type: false,
    content: false,
  }
  selectedContact.value = undefined
  selectedBusiness.value = undefined
}

const typeOptions = ref([
  { name: 'Note', value: 'Note' },
  { name: 'Call', value: 'Call' },
  { name: 'Email', value: 'Email' },
  { name: 'SMS', value: 'SMS' },
  { name: 'Inspection', value: 'Inspection' },
  { name: 'Offer', value: 'Offer' },
  { name: 'Meeting', value: 'Meeting' },
])

const validateForm = (): boolean => {
  errors.value.type = !noteData.value.type

  if (
    noteData.value.content === '<p></p>'
    || noteData.value.content === '<p> </p>'
    || noteData.value.content === ''
    || noteData.value.content === '<p style="margin-left: 0px!important;"></p>'
  ) {
    errors.value.content = true
  }
  else {
    errors.value.content = false
  }

  return !Object.values(errors.value).some(Boolean)
}

// Files Uploader
const filesPath = ref('notes')
const stagedFiles = ref([])
const uploaderRef = ref(null)
const isSubmitting = ref(false)
const uploadedFiles = ref<JSON[]>([])

const uploadAttachments = async () => {
  await uploaderRef.value?.uploadFiles()
}

// Associations
const { fetchContactLinkedBusinesses } = useContacts()
const { getAllPageAccessByBusinessId } = usePageAccess()
const contacts = ref()
const selectedContact = ref()

const { getAllBusinesses } = useBusiness()
const businesses = ref()
const selectedBusiness = ref()

// Save Note
const saveNote = async () => {
  submitted.value = true

  const isValid = validateForm()

  if (errors.value.type && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Type is required',
      life: 3000,
    })
  }

  if (errors.value.content && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Content is required',
      life: 3000,
    })
  }

  if (!isValid) return

  loading.value = true

  try {
    const contactId = selectedContact.value
    const businessId = selectedBusiness.value

    // Upload Attachments
    const noteAttachments = ref<JSON[]>([])
    console.log('Uploading note attachments')
    await uploadAttachments()

    if (uploadedFiles.value.length > 0) {
      noteAttachments.value = uploadedFiles.value
    }

    const { data, error } = await createNote(
      {
        type: noteData.value.type,
        content: noteData.value.content,
        showOnVendorReport: noteData.value.showOnVendorReport,
        attachments: noteAttachments.value,
      },
      contactId,
      businessId,
      props.contractId,
    )

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create note',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Note created successfully',
      life: 3000,
    })

    hideDialog()
    emit('refresh')
  }
  catch (error) {
    console.error('Error creating note:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create note',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}

const init = async () => {
  if (props.contactId) {
    selectedContact.value = props.contactId
  }

  if (props.businessId) {
    selectedBusiness.value = props.businessId
  }
}

onMounted(async () => {
  await init()

  if (route.path.startsWith('/contacts/') && props.contactId) {
    const resLinkedBusinesses = await fetchContactLinkedBusinesses(props.contactId)
    businesses.value = resLinkedBusinesses.map((business: {
      Business: Business
    }) => {
      return {
        id: business.Business.id,
        name: business.Business.name,
      }
    })
  }
  else {
    const resBusinesses = await getAllBusinesses()
    businesses.value = resBusinesses
  }

  if (route.path.startsWith('/businesses/') && props.businessId) {
    const resPageAccess = await getAllPageAccessByBusinessId(props.businessId)
    contacts.value = resPageAccess.data?.filter(item => item.contactId).map((record) => {
      return {
        id: record.contactId,
        name: record.firstName + ' ' + record.lastName,
      }
    })
  }
})
</script>
